import React from 'react';
import { useFeatureUsage } from '../../hooks/useFeatures';
import { FeatureCode } from '../../common/constants/featureCodes';

interface UsageBadgeProps {
	/** The feature code to display usage for */
	featureCode: FeatureCode | string;
	/** Visual variant */
	variant?: 'badge' | 'text' | 'progress';
	/** Show label text */
	showLabel?: boolean;
	/** Additional CSS classes */
	className?: string;
}

/**
 * Displays usage count for limited features.
 * Shows "used/limit" format with color coding based on remaining usage.
 *
 * @example
 * ```tsx
 * <UsageBadge featureCode={FeatureCodes.ORGANIZATION_SEND_MESSAGES} />
 * // Renders: "3/5"
 * ```
 */
/**
 * Displays current usage vs. limit for limit-type features.
 * Supports badge, text, and progress bar variants.
 * Color-codes based on proximity to limit (normal → warning → danger).
 *
 * @userstory US-FG-03
 */
const UsageBadge: React.FC<UsageBadgeProps> = ({
	featureCode,
	variant = 'badge',
	showLabel = false,
	className = '',
}) => {
	const usage = useFeatureUsage(featureCode);

	// Don't render for boolean features or unlimited features
	if (!usage || usage.limit === null) return null;

	const isAtLimit = usage.remaining === 0;
	const isNearLimit = usage.remaining !== null && usage.remaining <= Math.ceil(usage.limit * 0.2);
	const percentage = Math.min((usage.used / usage.limit) * 100, 100);

	// Determine color based on usage
	const getColorClass = () => {
		if (isAtLimit) return 'text-danger bg-danger-subtle';
		if (isNearLimit) return 'text-warning bg-warning-subtle';
		return 'text-muted';
	};

	const getProgressColor = () => {
		if (isAtLimit) return 'bg-danger';
		if (isNearLimit) return 'bg-warning';
		return 'bg-primary';
	};

	if (variant === 'progress') {
		return (
			<div className={`usage-progress ${className}`}>
				{showLabel && (
					<div className='d-flex justify-content-between small mb-1'>
						<span>Usage</span>
						<span>
							{usage.used}/{usage.limit}
						</span>
					</div>
				)}
				<div className='progress' style={{ height: '6px' }}>
					<div
						className={`progress-bar ${getProgressColor()}`}
						role='progressbar'
						style={{ width: `${percentage}%` }}
						aria-valuenow={usage.used}
						aria-valuemin={0}
						aria-valuemax={usage.limit}
					/>
				</div>
			</div>
		);
	}

	if (variant === 'text') {
		return (
			<span className={`usage-text ${isAtLimit ? 'text-danger' : ''} ${className}`}>
				{showLabel && 'Used: '}
				{usage.used}/{usage.limit}
			</span>
		);
	}

	// Default badge variant
	return (
		<span
			className={`badge rounded-pill ${getColorClass()} ${className}`}
			style={{ fontSize: '0.75em' }}>
			{usage.used}/{usage.limit}
		</span>
	);
};

export default UsageBadge;
